import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';

// Define common styles
const createStyle = (fontColor, bgColor, borderColor) => ({
  font: { name: 'Verdana Pro Light', bold: true, color: { argb: fontColor } },
  alignment: { horizontal: 'center' },
  fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } },
  border: {
    bottom: { style: 'thin', color: { argb: borderColor } },
    left: { style: 'thin', color: { argb: borderColor } },
    right: { style: 'thin', color: { argb: borderColor } },
    top: { style: 'thin', color: { argb: borderColor } }
  }
});

const createStyleNoBorder = (fontColor, bgColor) => ({
  font: { name: 'Verdana Pro Light', bold: true, color: { argb: fontColor } },
  alignment: { horizontal: 'center' },
  fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } }
});

const titleStyle = createStyle('FF000000', 'FFE2923D', 'FF7F7F7F');
const headerStyle = createStyle('FF000000', 'FFE2923D', 'FF7F7F7F');
const headerStyle1 = createStyle('FF000000', 'FFBFBFBF', 'FF7F7F7F');
const noBorderStyle = createStyleNoBorder('FF000000', 'FFBFBFBF');

const tableBorder = {
  top: { style: 'thin', color: { argb: 'FF7F7F7F' } },
  left: { style: 'thin', color: { argb: 'FF7F7F7F' } },
  bottom: { style: 'thin', color: { argb: 'FF7F7F7F' } },
  right: { style: 'thin', color: { argb: 'FF7F7F7F' } }
};

const estimateRowHeight = (text) => {
  const lineHeight = 15;
  const maxLineLength = 40;
  const numberOfLines = Math.ceil((text?.length || 0) / maxLineLength);
  return Math.max(lineHeight * numberOfLines, 20);
};

export async function exportToExcel(data, fileName = 'Debarkation_Data.xlsx') {
  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Flights');

    // Add title and merge cells
    worksheet.mergeCells('A1:J1');
    worksheet.getCell('A1').value = 'DEBARKATION FLIGHT REPORT';
    worksheet.getCell('A1').style = titleStyle;
    worksheet.getCell('A1').border = noBorderStyle;

    // Define headers
    const headers = ['FLIGHT', 'DST', 'PAX', 'OWN AIR', 'ETD', 'ATD', 'TERMINAL', 'CHECK-IN', 'ASS. REQ', 'REMARKS'];
    worksheet.getRow(5).values = headers;
    worksheet.getRow(5).eachCell(cell => (cell.style = headerStyle));

    // Add data starting from row 6
    let rowNumber = 6;
    data.forEach(item => {
      const {
        FLIGHT = '',
        DST = '',
        PAX = 0,
        OWNAIR = 0,
        ETD = '',
        ATD = '',
        departureTerminal = '',
        CHECK_IN = '',
        Assistant = 0,
        Remarks = ''
      } = item;
      worksheet.addRow([FLIGHT, DST, PAX, OWNAIR, ETD, ATD, departureTerminal, CHECK_IN, Assistant, Remarks]);
      rowNumber++;
    });

    // Set values for specific cells
    const I6Value = data[0]?.DEPARTURE ? moment(data[0].DEPARTURE).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
    worksheet.getCell('B3').value = 'Date';
    worksheet.getCell('D3').value = I6Value;
    worksheet.getCell('G3').value = 'Ship';
    worksheet.getCell('I3').value = 'Port';

    // Apply header style to A3:J3
    for (let col = 1; col <= 10; col++) {
      const cell = worksheet.getCell(`${String.fromCharCode(64 + col)}3`);
      cell.style = headerStyle1;
      cell.border = noBorderStyle;
    }

    // Merge rows A2 and A4
    worksheet.mergeCells('A2:J2');
    worksheet.mergeCells('A4:J4');

    // Apply borders to data rows
    const lastRow = worksheet.lastRow.number;
    for (let row = 5; row <= lastRow; row++) {
      worksheet.getRow(row).eachCell({ includeEmpty: false }, cell => (cell.border = tableBorder));
    }

    // Set column properties
    worksheet.columns.forEach((column, index) => {
      column.width = 10;
      column.hidden = false;
      column.alignment = { horizontal: 'center' };
    });

    // Set specific column widths
    worksheet.getColumn(10).width = 40;
    worksheet.getColumn(9).width = 11;
    worksheet.getColumn(8).width = 10;
    worksheet.getColumn(2).width = 6;
    worksheet.getColumn(3).width = 6;
    worksheet.getColumn(4).width = 10;
    worksheet.getColumn(5).width = 6;
    worksheet.getColumn(6).width = 6;
    worksheet.getColumn(7).width = 12;

        // Calculate sum of Total Pax
    const totalPax = data.reduce((sum, item) => sum + (item.PAX || 0), 0);

    // Add Total Pax row before remarks table
    worksheet.mergeCells(`C${rowNumber}:D${rowNumber}`);
    worksheet.getCell(`C${rowNumber}`).value = `Total Pax: ${totalPax}`;
    worksheet.getCell(`C${rowNumber}`).style = createStyle('FF000000', 'FFE2923D', 'FF7F7F7F');
    worksheet.getCell(`C${rowNumber}`).border = noBorderStyle;
    worksheet.getCell(`C${rowNumber}`).alignment = { horizontal: 'right' };
    rowNumber++;

    // Remarks section
    const remarksStartRow = rowNumber + 2;
    worksheet.mergeCells(`A${remarksStartRow - 2}:J${remarksStartRow - 1}`);
    worksheet.mergeCells(`A${remarksStartRow}:J${remarksStartRow}`);
    worksheet.getCell(`A${remarksStartRow}`).value = 'REMARKS';
    worksheet.getCell(`A${remarksStartRow}`).style = createStyle('FF000000', 'FFBFBFBF', 'FF7F7F7F');
    worksheet.getCell(`A${remarksStartRow}`).border = noBorderStyle;

    worksheet.getRow(remarksStartRow + 1).values = [
      'FLIGHT', 'FIRST NAME', 'FIRST NAME', 'LAST NAME', 'LAST NAME', 'BOOKING #', 'BOOKING #', 'ASSISTANCE', 'ASSISTANCE', 'ASSISTANCE'
    ];
    worksheet.getRow(remarksStartRow + 1).eachCell({ includeEmpty: true }, cell => (cell.style = headerStyle));

    // Merge columns for headers
    worksheet.mergeCells(`B${remarksStartRow + 1}:C${remarksStartRow + 1}`);
    worksheet.mergeCells(`D${remarksStartRow + 1}:E${remarksStartRow + 1}`);
    worksheet.mergeCells(`F${remarksStartRow + 1}:G${remarksStartRow + 1}`);
    worksheet.mergeCells(`H${remarksStartRow + 1}:J${remarksStartRow + 1}`);

    // Add remarks data
    let remarksRow = remarksStartRow + 2;
    data.forEach(item => {
      const { details = [] } = item;
      details.forEach(detail => {
        if (detail.remarks?.trim() !== '') {
          const row = worksheet.addRow([
            detail.flightNumber, detail.firstName, null, detail.lastName, null, detail.bookingNumber, null, detail.remarks, null
          ]);
          const remarksHeight = estimateRowHeight(detail.remarks);
          worksheet.getRow(remarksRow).height = Math.max(worksheet.getRow(remarksRow).height || 15, remarksHeight);
          worksheet.getRow(remarksRow).eachCell(cell => (cell.style = {
            font: { name: 'Verdana Pro Light', color: { argb: 'FF000000' } },
            alignment: { horizontal: 'left', wrapText: true },
            border: tableBorder
          }));
          remarksRow++;
        }
      });
    });

    // Merge cells for remarks data
    for (let row = remarksStartRow + 2; row < remarksRow; row++) {
      worksheet.mergeCells(`B${row}:C${row}`);
      worksheet.mergeCells(`D${row}:E${row}`);
      worksheet.mergeCells(`F${row}:G${row}`);
      worksheet.mergeCells(`H${row}:J${row}`);
    }

    // Additional Info section
    const additionalInfoStartRow = remarksRow + 2;
    worksheet.mergeCells(`A${additionalInfoStartRow - 2}:J${additionalInfoStartRow - 1}`);
    worksheet.mergeCells(`A${additionalInfoStartRow}:J${additionalInfoStartRow}`);
    worksheet.getCell(`A${additionalInfoStartRow}`).value = 'OPERATIONAL DETAILS';
    worksheet.getCell(`A${additionalInfoStartRow}`).style = createStyle('FF000000', 'FFBFBFBF', 'FF7F7F7F');
    worksheet.getCell(`A${additionalInfoStartRow}`).border = noBorderStyle;

    worksheet.getRow(additionalInfoStartRow + 1).values = [
      'FLIGHT', 'FIRST NAME', 'FIRST NAME', 'LAST NAME', 'LAST NAME', 'BOOKING #', 'BOOKING #', 'ADDITIONAL INFO', 'ADDITIONAL INFO', 'ADDITIONAL INFO'
    ];
    worksheet.getRow(additionalInfoStartRow + 1).eachCell({ includeEmpty: true }, cell => (cell.style = headerStyle));

    // Merge columns for headers
    worksheet.mergeCells(`B${additionalInfoStartRow + 1}:C${additionalInfoStartRow + 1}`);
    worksheet.mergeCells(`D${additionalInfoStartRow + 1}:E${additionalInfoStartRow + 1}`);
    worksheet.mergeCells(`F${additionalInfoStartRow + 1}:G${additionalInfoStartRow + 1}`);
    worksheet.mergeCells(`H${additionalInfoStartRow + 1}:J${additionalInfoStartRow + 1}`);

    // Add additional info data
    let additionalInfoRow = additionalInfoStartRow + 2;
    data.forEach(item => {
      const { infos = [] } = item;
      infos.forEach(info => {
        if (info.additionalInfo?.trim() !== '') {
          worksheet.addRow([
            info.flightNumber, info.firstName, null, info.lastName, null, info.bookingNumber, null, info.additionalInfo, null
          ]);
          const infoHeight = estimateRowHeight(info.additionalInfo);
          worksheet.getRow(additionalInfoRow).height = Math.max(worksheet.getRow(additionalInfoRow).height || 15, infoHeight);
          worksheet.getRow(additionalInfoRow).eachCell(cell => (cell.style = {
            font: { name: 'Verdana Pro Light', color: { argb: 'FF000000' } },
            alignment: { horizontal: 'left', wrapText: true },
            border: tableBorder
          }));
          additionalInfoRow++;
               }
      });
    });

    // Merge cells for additional info data
    for (let row = additionalInfoStartRow + 2; row < additionalInfoRow; row++) {
      worksheet.mergeCells(`B${row}:C${row}`);
      worksheet.mergeCells(`D${row}:E${row}`);
      worksheet.mergeCells(`F${row}:G${row}`);
      worksheet.mergeCells(`H${row}:J${row}`);
    }

    // Save the file
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), fileName);
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    alert('Failed to export data to Excel. Please try again.');
  }
}

export function prepareAndExportData(filteredData) {
  if (typeof filteredData !== 'object' || filteredData === null) {
    console.error('Invalid filteredData:', filteredData);
    alert('No valid data to export.');
    return;
  }

  const exportData = [];
  Object.keys(filteredData).forEach(cruiseLine => {
    const flightGroups = filteredData[cruiseLine];
    Object.keys(flightGroups).forEach(flightKey => {
      const group = flightGroups[flightKey];
      const dataItem = group.data || [];
      if (!dataItem.length) return;

      const firstItem = dataItem[0]?.attributes || {};
      const notes = dataItem.map(item => item.attributes.note || '');
      const countOwnAir = notes.reduce((count, note) => count + (note.match(/OWN AIR/gi) || []).length, 0);

      const remarks = dataItem
        .map(item => ({
          flightNumber: item.attributes.flightNumber || '',
          firstName: item.attributes.firstName || '',
          lastName: item.attributes.guestsLastName || '',
          bookingNumber: item.attributes.bookingNumber || '',
          remarks: item.attributes.remarks || '',
          additionalInfo: item.attributes.additionalInfo || ''
        }))
        .filter(detail => detail.remarks?.trim() !== '');

      const additionalInfo = dataItem
        .map(item => ({
          flightNumber: item.attributes.flightNumber || '',
          firstName: item.attributes.firstName || '',
          lastName: item.attributes.guestsLastName || '',
          bookingNumber: item.attributes.bookingNumber || '',
          additionalInfo: item.attributes.additionalInfo || ''
        }))
        .filter(info => info.additionalInfo?.trim() !== '');

      const assistantCount = remarks.length;

      exportData.push({
        FLIGHT: firstItem.flightNumber || '',
        DST: firstItem.arrivalAirport || '',
        PAX: dataItem.length,
        OWNAIR: countOwnAir,
        ETD: (firstItem.departureTime || '').substring(0, 5) || '',
        ATD: firstItem.actualGateDeparture ? moment(firstItem.actualGateDeparture).format('HH:mm') : '',
        departureTerminal: firstItem.departureTerminal || '',
        'CHECK_IN': firstItem.checkIn || '',
        Assistant: assistantCount,
        Remarks: '',
        DEPARTURE: firstItem.departureDate ? moment(firstItem.departureDate).format('YYYY-MM-DD') : '',
        details: remarks,
        infos: additionalInfo
      });
    });
  });

  if (!exportData.length) {
    console.warn('No valid data to export');
    alert('No data available to export.');
    return;
  }

  const earliestDeparture = exportData.reduce((earliest, current) => {
    const currentDeparture = current.DEPARTURE ? moment(current.DEPARTURE, 'YYYY-MM-DD') : null;
    if (!earliest || (currentDeparture && currentDeparture.isBefore(earliest))) {
      return currentDeparture;
    }
    return earliest;
  }, null);

  const fileNameDate = earliestDeparture ? earliestDeparture.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
  const fileName = `${fileNameDate}_Debark.xlsx`;

  exportToExcel(exportData, fileName);
}

export async function exportFullDataToExcel(data, fileNameDate = moment().format('YYYY-MM-DD'), prefix = 'Debark') {
  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Complete Data');

    // Headers mapped to backend schema attributes for Debarkation
    const headers = [
      'cruiseLine', 'guestsLastName', 'firstName', 'bookingNumber', 'flightNumber',
      'language', 'departureAirport', 'arrivalAirport', 'cabinNumber', 'note',
      'departureDate', 'arrivalDate', 'departureTime', 'arrivalTime', 'guestStatus',
      'additionalInfo', 'remarks', 'status', 'departureTerminal', 'baggage',
      'codeShare', 'departureGateDelayMinutes', 'arrivalGateDelayMinutes',
      'actualRunwayDeparture', 'estimatedRunwayArrival', 'actualGateDeparture',
      'estimatedGateDeparture', 'buses', 'feeder'
    ];

    worksheet.getRow(1).values = headers;
    worksheet.getRow(1).eachCell(cell => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'center' };
    });

    data.forEach(item => {
      const attr = item.attributes || item;
      const rowData = headers.map(header => attr[header] || '');
      worksheet.addRow(rowData);
    });

    worksheet.columns.forEach(column => {
      column.width = 15;
    });

    const fileName = `${fileNameDate}_${prefix}_Complete.xlsx`;
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), fileName);
  } catch (error) {
    console.error('Error exporting complete data to Excel:', error);
    alert('Failed to export complete data to Excel. Please try again.');
  }
}